import type { GridState, GridApi } from "ag-grid-community"
import { useEffect, useState, useMemo, useRef, useCallback } from "react"
import { useSessionStorage } from "usehooks-ts"

import {
  AVAILABILITY_TABLE_ID,
  COLLECTIONS_CUSTOMERS_TABLE_ID,
  COLLECTIONS_DEMAND_PLAN_TAB_TABLE_ID,
  COLLECTIONS_OVERVIEW_TABLE_ID,
  COLLECTIONS_PERFORMANCE_TABLE_ID,
  FORECAST_CALENDAR_PRODUCT_TABLE_ID,
  INCOMING_ORDERS_CALENDAR_TABLE_ID,
  INVENTORY_CALENDAR_PRODUCT_TABLE_ID,
  INVENTORY_PAGE_TABLE_ID,
  NOOS_BUY_TABLE_ID,
  NOOS_BUY_CAPACITY_TABLE_ID,
  NOOS_CAPACITY_PLANNING_TABLE_ID,
  NOOS_PRODUCT_TABLE_ID,
  NOOS_NEW_PRODUCTS_TABLE_ID,
  NOOS_FORECAST_UPDATES_TABLE_ID,
  PM_LINKED_VARIANTS_TABLE_ID,
  PM_REGULAR_VARIANTS_TABLE_ID,
  PURCHASE_ORDER_UPDATE_TABLE_ID,
  PURCHASE_PLAN_GREIGE_TABLE_ID,
  PURCHASE_PLAN_TABLE_ID,
  REPLENISHMENT_SUGGESTIONS_TABLE_ID,
  SUGGESTED_VARIANT_ACTIVATIONS_TABLE_ID,
  PURCHASE_PLAN_SKU_QUANTITIES_TABLE_ID,
  PURCHASE_PLAN_CAPACITY_TABLE_ID,
  SUPPLY_PLAN_COLLECTION_CAPACITY_TABLE_ID,
  SUPPLY_PLAN_COLLECTION_GREIGE_TABLE_ID,
  NOOS_SUPPLY_PLAN_GREIGE_TABLE_ID,
  NOOS_SUPPLY_PLAN_TRIGGERS_TABLE_ID,
  NOOS_SUPPLY_PLAN_AVAILABILITY_TABLE_ID,
} from "~/constants"
import { useFilters } from "~/hooks/useFilters"
import { useSkuStore } from "~/providers/sku-store-provider"
import { useProductTableAggregations, useProductTableDisplayColumns } from "~/hooks/useProductTable"
import {
  gridOwnedChooserOf,
  useReadTableCapabilities,
  useTableRegistry,
} from "~/components/planning/TableRegistryContext"
import { type FileRouteTypes } from "~/routeTree.gen"
import { useLocation, useSearch } from "@tanstack/react-router"
import { trackEvent } from "~/hooks/useTrackingService"
import type { SavedTableView } from "~/server/api/routers/tableView"
import { parseViewParam, targetNamesView } from "~/hooks/useViewRoute"
import { resolveViewScope } from "~/server/api/utils/viewAccess"
import { useUser } from "~/providers/userProvider"
import { api, type RouterInputs } from "~/utils/api"
import { toast } from "react-toastify"

import {
  subscribeGridApiChange,
  subscribeViewChange,
  signalViewChange,
  useGridStateStorage,
  useGridDerivedValue,
  setIsApplyingView,
  normalizeGridStateForSave,
  isApplyingView,
  getAppliedViewBaseline,
  clearAppliedViewBaseline,
  signalGridValueChange,
  gridStatesEqual,
  PERSISTED_GRID_STATE_EVENTS,
  deriveDisplayColumnsFromGridState,
} from "~/hooks/useGridState"
import { validateAndNormalizeAggregations } from "~/utils/productTableUtils"

/** Where a view save started: the pill menu, the bar's save button, the filter row, or ⌘K */
export type ViewSaveSource = "Pill" | "Save Button" | "Filter Row" | "Search" | "Overflow Menu"

type SaveViewInput = RouterInputs["savedTableView"]["upsert"]

const VIEWS_FRESH_MS = 10_000

const TABLE_VIEW_META: {
  pathnames: FileRouteTypes["to"][]
  id: string
  tableId: string
  matchTableId?: (tableId: string) => boolean
}[] = [
  {
    pathnames: ["/insights/forecast-calendar"],
    id: "forecastCalendar",
    tableId: FORECAST_CALENDAR_PRODUCT_TABLE_ID,
  },
  {
    // The same calendar as the Demand module's Sales & Demand tab, sharing the "forecastCalendar"
    // page id so saved views resolve on both routes. A separate entry rather than a second
    // pathname above: multi-pathname entries lose the stable currentView key and
    // getViewMetaForTableId skips them.
    pathnames: ["/demand/sales-and-demand"],
    id: "forecastCalendar",
    tableId: FORECAST_CALENDAR_PRODUCT_TABLE_ID,
  },
  {
    // The old "regularVariants" alias was removed: those views are migrated to page
    // "inventory" (see migration migrate_regular_variants_views_to_inventory). The legacy
    // PM → Active page no longer uses the saved-view system.
    pathnames: ["/insights/inventory-overview"],
    id: "inventory",
    tableId: INVENTORY_PAGE_TABLE_ID,
  },
  {
    // Inventory Overview + Calendar share the /insights/inventory-overview URL (preset views),
    // so this entry must be resolved via tableIdOverride. The distinct pathname keeps its
    // saved-view page id and currentView storage key separate from the Overview preset.
    pathnames: ["/insights/inventory-overview/calendar"],
    id: "inventoryCalendar",
    tableId: INVENTORY_CALENDAR_PRODUCT_TABLE_ID,
  },
  {
    pathnames: ["/insights/availability"],
    id: "availability",
    tableId: AVAILABILITY_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/$purchasePlanId/capacity"],
    id: "supplyPlanCapacity",
    tableId: PURCHASE_PLAN_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/$purchasePlanId/greige"],
    id: "supplyPlanGreige",
    tableId: PURCHASE_PLAN_GREIGE_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/$purchasePlanId/quantities"],
    id: "supplyPlanQuantities",
    tableId: PURCHASE_PLAN_SKU_QUANTITIES_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/$purchasePlanId/capacity"],
    id: "supplyPlanCapacity",
    tableId: PURCHASE_PLAN_CAPACITY_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/collection/$collectionId/capacity"],
    id: "supplyPlanCollectionCapacity",
    tableId: SUPPLY_PLAN_COLLECTION_CAPACITY_TABLE_ID,
  },
  {
    // Shares the buy-level Greige views: same columns, so a view saved in a buy works across the season.
    pathnames: ["/procurement/supply-plan/collection/$collectionId/greige"],
    id: "supplyPlanGreige",
    tableId: SUPPLY_PLAN_COLLECTION_GREIGE_TABLE_ID,
  },
  {
    pathnames: ["/product-management/suggested-activations", "/product-management"],
    id: "suggestedVariantActivations",
    tableId: SUGGESTED_VARIANT_ACTIVATIONS_TABLE_ID,
  },
  {
    pathnames: ["/product-management/active"],
    id: "regularVariants",
    tableId: PM_REGULAR_VARIANTS_TABLE_ID,
  },
  {
    pathnames: ["/product-management/linked-variants"],
    id: "linkedVariants",
    tableId: PM_LINKED_VARIANTS_TABLE_ID,
  },
  // {
  //   pathnames: ["/procurement/incoming-orders/list"],
  //   id: "incomingOrders",
  //   tableId: INCOMING_ORDERS_LIST_TABLE_ID,
  // },
  {
    pathnames: ["/procurement/incoming-orders/calendar"],
    id: "incomingOrdersCalendar",
    tableId: INCOMING_ORDERS_CALENDAR_TABLE_ID,
  },
  {
    pathnames: [
      "/procurement/order-suggestions",
      "/procurement/order-suggestions/replenishment-suggestions",
    ],
    id: "orderSuggestions",
    tableId: REPLENISHMENT_SUGGESTIONS_TABLE_ID,
  },
  {
    pathnames: ["/demand-planning/fashion/$collectionId"],
    id: "collectionsOverview",
    tableId: COLLECTIONS_OVERVIEW_TABLE_ID,
  },
  {
    pathnames: ["/demand-planning/fashion/$collectionId/demand"],
    id: "collectionsDemand",
    tableId: COLLECTIONS_DEMAND_PLAN_TAB_TABLE_ID,
  },
  {
    pathnames: ["/demand-planning/fashion/$collectionId/customers"],
    id: "collectionsCustomers",
    tableId: COLLECTIONS_CUSTOMERS_TABLE_ID,
  },
  {
    pathnames: ["/demand-planning/fashion/$collectionId/performance"],
    id: "collectionsPerformance",
    tableId: COLLECTIONS_PERFORMANCE_TABLE_ID,
  },
  {
    pathnames: ["/procurement/purchase-orders/update/$purchaseOrderId"],
    id: "purchaseOrderUpdate",
    tableId: PURCHASE_ORDER_UPDATE_TABLE_ID,
  },
  {
    pathnames: ["/demand-planning/noos/$collectionId"],
    // TODO: migrate saved views in DB from page="forecast" to page="noosOverview", then rename this id
    id: "forecast",
    tableId: NOOS_PRODUCT_TABLE_ID,
  },
  {
    pathnames: ["/demand-planning/noos/$collectionId/new-variants"],
    id: "noosNewVariants",
    tableId: NOOS_NEW_PRODUCTS_TABLE_ID,
  },
  {
    pathnames: ["/demand-planning/noos/$collectionId/forecast-updates"],
    id: "noosForecastUpdates",
    tableId: NOOS_FORECAST_UPDATES_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/noos/$supplyPlanId/buys/$buyId/quantities"],
    id: "noosBuyQuantities",
    tableId: NOOS_BUY_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/noos/$supplyPlanId/buys/$buyId/capacity"],
    id: "noosBuyCapacity",
    tableId: NOOS_BUY_CAPACITY_TABLE_ID,
  },
  {
    // Matched by pathname: the NOOS capacity-planning tab passes an instance-scoped
    // `${NOOS_CAPACITY_PLANNING_TABLE_ID}-${supplyPlanId}` as tableIdOverride, so the
    // canonical id here only anchors the saved-view page.
    pathnames: ["/procurement/supply-plan/noos/$supplyPlanId/capacity-planning"],
    id: "noosCapacityPlanning",
    tableId: NOOS_CAPACITY_PLANNING_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/noos/$supplyPlanId/greige"],
    id: "noosGreige",
    tableId: NOOS_SUPPLY_PLAN_GREIGE_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/noos/$supplyPlanId/triggers"],
    id: "noosTriggers",
    tableId: NOOS_SUPPLY_PLAN_TRIGGERS_TABLE_ID,
  },
  {
    pathnames: ["/procurement/supply-plan/noos/$supplyPlanId/availability"],
    id: "noosAvailability",
    tableId: NOOS_SUPPLY_PLAN_AVAILABILITY_TABLE_ID,
  },
  // These switch by preset, not route, so they share a pathname and are told apart by grid id.
  {
    pathnames: ["/demand/fashion/$collectionId/demand"],
    id: "demand2FashionTotal",
    tableId: "demand2-fashion-total",
    matchTableId: (id) => id.startsWith("demand2-total-"),
  },
  {
    pathnames: ["/demand/fashion/$collectionId/demand"],
    id: "demand2FashionPreorders",
    tableId: "demand2-fashion-preorders",
    matchTableId: (id) => id.startsWith("demand2-preorders-"),
  },
  {
    pathnames: ["/demand/noos/$collectionId/demand"],
    id: "demand2NoosTotal",
    tableId: "demand2-noos-total",
    matchTableId: (id) => id.startsWith("demand2-total-"),
  },
  {
    pathnames: ["/demand/noos/$collectionId/demand"],
    id: "demand2NoosPreorders",
    tableId: "demand2-noos-preorders",
    matchTableId: (id) => id.startsWith("demand2-preorders-"),
  },
]

/**
 * Look up the canonical TABLE_VIEW_META entry for a given tableId.
 * Returns the entry's page id and the storage key used for `currentView` persistence —
 * useful when a parent (e.g. the Analysis tab) needs to write/read a saved view for a
 * preset that isn't currently mounted.
 */
export function getViewMetaForTableId(
  tableId: string,
): { page: string; locationId: string } | null {
  const entry = TABLE_VIEW_META.find((m) => m.tableId === tableId)
  if (!entry) return null
  if (entry.pathnames.length !== 1) return null
  const scope = entry.matchTableId ? `${entry.pathnames[0]}#${entry.id}` : entry.pathnames[0]
  return { page: entry.id, locationId: scope + "-current-view" }
}

// View-side state is immutable per reference; cache its normalization since the
// selector runs on every render/grid event.
const normalizedViewGridStateCache = new WeakMap<GridState, GridState>()
function normalizeViewGridStateCached(state: GridState): GridState {
  const cached = normalizedViewGridStateCache.get(state)
  if (cached) return cached
  const normalized = normalizeGridStateForSave(state)
  normalizedViewGridStateCache.set(state, normalized)
  return normalized
}

/**
 * @param applyOnMount - Set to `false` for components that mount dynamically after page load
 *   (e.g. inside a popover or dialog). The default (`true`) runs a one-time effect that
 *   restores the saved view state, which writes to shared state and can cause unnecessary
 *   re-renders in components like ProductTableV2.
 * @param onViewChange - Callback fired after the active view changes (view switch or back-to-default).
 *   Runs with a short delay so ag-grid's async setState has settled.
 */
export const useView = ({
  applyOnMount = true,
  onViewChange: onViewChangeCallback,
  filtersEnabled = true,
  channelEnabled = true,
  warehouseEnabled = true,
  tableIdOverride,
  additionalPages,
}: {
  applyOnMount?: boolean
  onViewChange?: () => void
  filtersEnabled?: boolean
  channelEnabled?: boolean
  warehouseEnabled?: boolean
  /**
   * When provided, look up TABLE_VIEW_META by tableId instead of the current pathname.
   * Used by routes (e.g. the merged Analysis tab) where the active "logical page"
   * is determined by an active preset rather than by URL.
   */
  tableIdOverride?: string
  /**
   * Additional page ids to fetch saved views for. Used by merged tabs (e.g. Analysis)
   * to surface saved views from sibling presets in one unified pill row.
   */
  additionalPages?: string[]
} = {}) => {
  const location = useLocation()
  // Non-strict: `view` is declared on /_auth, and this hook runs on many routes.
  const viewParam = useSearch({ strict: false, select: (s) => s.view }) as
    | string
    | number
    | undefined
  const { clearAll } = useFilters()
  const { permissions, userId } = useUser()
  const utils = api.useUtils()

  const tableView = useMemo(() => {
    const matchesPathname = (meta: (typeof TABLE_VIEW_META)[number]) => {
      const regexPathnames = meta.pathnames.map<RegExp>(
        (val) =>
          new RegExp(
            val
              .replaceAll(/\$(.*?)(?=\/|$)/g, "[\\w,-]*") // Replace all placeholders with the regex equivalent for 'word'
              .replaceAll(/$/g, "$"), // Replace all line endings with their regex equivalent
          ),
      )
      return regexPathnames.some((regex) => regex.test(location.pathname))
    }

    // Try the explicit override first. Fall back to pathname matching if it doesn't
    // match any TABLE_VIEW_META entry — pages may scope their V2 tableId per-instance
    // (e.g. `${TABLE_ID}-${demandPlanId}`) while saved views are tracked at the
    // canonical route level.
    if (tableIdOverride) {
      const byTableId = TABLE_VIEW_META.find((meta) =>
        // Two routes can host a table of the same family, so the route has to agree first.
        meta.matchTableId
          ? meta.matchTableId(tableIdOverride) && matchesPathname(meta)
          : meta.tableId === tableIdOverride,
      )
      if (byTableId) return byTableId
    }
    return TABLE_VIEW_META.find(matchesPathname)
  }, [location, tableIdOverride])

  const page = tableView?.id ?? ""

  // Grid operations must use the id the grid actually registers under — pages may scope it
  // per-instance (e.g. `${TABLE_ID}-${demandPlanId}`); `page` and `locationId` stay canonical.
  const gridTableId = tableIdOverride ?? tableView?.tableId ?? ""

  const locationId = useMemo(() => {
    // Siblings on one route need distinct keys, or switching carries one's applied view onto the other.
    if (tableView?.matchTableId) return `${tableView.pathnames[0]}#${tableView.id}-current-view`
    return (
      (tableView?.pathnames.length === 1
        ? tableView.pathnames[0]
        : location.pathname + location.hash) + "-current-view"
    )
  }, [tableView, location.pathname, location.hash])

  const [currentView, setCurrentView, removeCurrentView] = useSessionStorage<
    SavedTableView | undefined
  >(locationId, undefined)

  const [aggregations, setAggregations, clearAggregations] =
    useProductTableAggregations(gridTableId)
  const [, setDisplayedColumnIds, clearDisplayedColumnIds] =
    useProductTableDisplayColumns(gridTableId)
  const registered = useTableRegistry(gridTableId)
  const readCapabilities = useReadTableCapabilities()
  const gridOwnedChooser = gridOwnedChooserOf(registered, gridTableId)
  const { setFilters, filters } = useFilters()

  const { getInitialState, getCurrentState, applyState, getGridApi, clearState, saveState } =
    useGridStateStorage(gridTableId)

  const updateWarehouseId = useSkuStore((state) => state.updateWarehouseId)
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)

  const updateChannel = useSkuStore((state) => state.updateChannel)
  const selectedChannel = useSkuStore((state) => state.channel)

  const backToDefault = () => {
    removeCurrentView()

    clearAggregations()
    if (gridOwnedChooser) gridOwnedChooser.showColumns([...gridOwnedChooser.defaultColumnIds])
    else clearDisplayedColumnIds()
    clearState()

    if (filtersEnabled) clearAll()
    if (channelEnabled) updateChannel(undefined)
    if (warehouseEnabled) updateWarehouseId(undefined)

    if (gridTableId) setTimeout(() => signalViewChange(gridTableId), 0)
  }

  function isFiltersSame(): boolean {
    if (!filtersEnabled) return true
    if (currentView?.filters?.length !== filters.length) {
      return false
    }

    for (let i = 0; i < (currentView?.filters?.length ?? 0); i++) {
      if (currentView?.filters?.[i]?.value !== filters?.[i]?.value) {
        return false
      }
      if ((currentView?.filters?.[i]?.gate ?? "and") !== (filters?.[i]?.gate ?? "and")) {
        return false
      }
      if (currentView?.filters?.[i]?.groupId !== filters?.[i]?.groupId) {
        return false
      }
    }

    return true
  }

  function isChannelSame(): boolean {
    if (!channelEnabled) return true
    return currentView?.channel == selectedChannel
  }

  function isWarehouseSame(): boolean {
    if (!warehouseEnabled) return true
    return currentView?.warehouseId == selectedWarehouseId
  }

  // Whether the live grid state matches the saved view. Written as a pure selector
  // over the grid API so it can be driven by `useGridDerivedValue`, which re-evaluates
  // on the grid-mutation events that affect persisted state. We normalize and ignore
  // transient keys, and only compare fields saved as part of the view to avoid false
  // positives from unrelated grid state changes.
  const isGridStateSame = useCallback(
    (api: GridApi | undefined): boolean => {
      if (gridTableId && isApplyingView(gridTableId)) return true
      if (!api || api?.isDestroyed?.()) return true

      const currentGridState = api.getState()
      // Prefer the post-apply baseline over the raw DB blob (see `appliedViewBaselines` in useGridState).
      const viewGridState =
        // The grid id, not the entry's canonical one: that is what writes the baseline.
        (gridTableId ? getAppliedViewBaseline(gridTableId) : undefined) ?? currentView?.gridState

      if (!currentGridState && !viewGridState) return true
      if (!currentGridState || !viewGridState) return false

      // Normalize both sides identically — older DB blobs predate some normalization steps.
      const normalizedCurrentGridState = normalizeGridStateForSave(currentGridState)
      const normalizedViewGridState = normalizeViewGridStateCached(viewGridState)

      // Union both sides — a column hidden in only one state shouldn't cause a width mismatch.
      const hiddenColIds = new Set([
        ...(normalizedCurrentGridState.columnVisibility?.hiddenColIds ?? []),
        ...(normalizedViewGridState.columnVisibility?.hiddenColIds ?? []),
      ])

      return gridStatesEqual(normalizedViewGridState, normalizedCurrentGridState, hiddenColIds)
    },
    [gridTableId, currentView],
  )

  // Recomputes on the grid events that change persisted state (and whenever the
  // selector identity changes, i.e. when `currentView` changes).
  const gridStateSame = useGridDerivedValue(
    gridTableId,
    isGridStateSame,
    PERSISTED_GRID_STATE_EVENTS,
  )

  // True when the grid has any active column-header filter (e.g. an ad-hoc month
  // filter on the availability report). Returns a primitive so it stays stable for
  // useSyncExternalStore. Lets the toolbar surface a "Clear view" affordance even
  // when no saved view / pill filter / channel / warehouse is active.
  const hasActiveGridFilterSelector = useCallback((api: GridApi | undefined): boolean => {
    if (!api || api?.isDestroyed?.()) return false
    const model = api.getFilterModel()
    return !!model && Object.keys(model).length > 0
  }, [])
  const hasActiveGridFilter = useGridDerivedValue(
    gridTableId,
    hasActiveGridFilterSelector,
    PERSISTED_GRID_STATE_EVENTS,
  )

  const isCurrentViewChanged = useMemo(() => {
    if (currentView === undefined) {
      return (
        (filtersEnabled && filters.length > 0) ||
        (channelEnabled && !!selectedChannel) ||
        (warehouseEnabled && !!selectedWarehouseId)
      )
    }
    return !isFiltersSame() || !isChannelSame() || !isWarehouseSame() || !gridStateSame
  }, [
    currentView,
    selectedWarehouseId,
    selectedChannel,
    filtersEnabled,
    channelEnabled,
    warehouseEnabled,
    filters,
    gridStateSame,
  ])

  /**
   * Applies a saved view to the current table.
   * Handles grid state, column visibility, sorting, aggregations, and filters.
   *
   * @param isMountRestore - Set when re-applying the stored view on mount (after a reload or
   *   remount — e.g. switching warehouse triggers a full-screen reload of the app). In that case
   *   we only re-push the grid state, because the ag-grid API isn't ready yet on first mount.
   *   Everything else already restores from its own store and must NOT be re-applied from the
   *   stored snapshot, or we'd overwrite changes made since the view was applied:
   *     - filters            → DB (productTableFilters)
   *     - columns/sort/gridState → sessionStorage (useGridStateStorage)
   *     - warehouse/channel  → sku-store sessionStorage
   *   Re-applying the snapshot here is what reset active filters on warehouse switch (HAK-1734).
   */
  const viewChange = (
    value: SavedTableView | undefined,
    { isMountRestore = false }: { isMountRestore?: boolean } = {},
  ) => {
    if (!value) return

    if (gridTableId) {
      setIsApplyingView(gridTableId, true)
    }

    setCurrentView(value)

    const viewColumnIds = deriveDisplayColumnsFromGridState(value.gridState)
    if (value.gridState) {
      applyState(value.gridState, { keepPagination: isMountRestore })
      saveState(value.gridState)
      // Read now: the mount restore calls a `viewChange` from before the table registered.
      if (gridTableId) readCapabilities(gridTableId)?.onViewApplied?.(viewColumnIds)
    }

    if (!isMountRestore) {
      // Tables that still build their columns from `displayedColumnIds` need it re-seeded from
      // the view's gridState; where the grid owns visibility, the gridState apply is the view.
      if (!gridOwnedChooser) {
        if (viewColumnIds.length > 0) setDisplayedColumnIds(viewColumnIds)
        else clearDisplayedColumnIds()
      }

      setAggregations(validateAndNormalizeAggregations(value.aggregationMethods))
      if (warehouseEnabled) updateWarehouseId(value.warehouseId ?? undefined)
      if (channelEnabled) updateChannel(value.channel ?? undefined)

      if (filtersEnabled) setFilters(value.filters)
    }

    if (gridTableId) setTimeout(() => signalViewChange(gridTableId), 0)
  }

  const saveView = api.savedTableView.upsert.useMutation({
    onSuccess: (newView) => {
      refetch()
      utils.savedTableView.get.invalidate()
      toast.success(`The view ${newView.name} was successfully saved.`)
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const {
    data: savedViews,
    refetch,
    dataUpdatedAt,
  } = api.savedTableView.get.useQuery(
    {
      pages: [page, ...(additionalPages ?? [])].filter((p, i, arr) => p && arr.indexOf(p) === i),
      isHakio: permissions.isStaff,
    },
    { enabled: !!tableView },
  )

  function refetchViews() {
    refetch()
  }

  /** Refetches unless fetched moments ago; returns the refetch, or `undefined` when skipped. */
  function refreshViewsIfStale() {
    return Date.now() - dataUpdatedAt > VIEWS_FRESH_MS ? refetch() : undefined
  }

  const [viewName, setViewName] = useState(currentView?.name ?? "")
  const [viewDescription, setViewDescription] = useState(currentView?.description ?? "")
  const [isGlobalView, setIsGlobalView] = useState(currentView?.userId === null)

  const trackViewSaved = (input: SaveViewInput, source: ViewSaveSource) =>
    trackEvent("View Saved", {
      "View Name": input.name,
      "Table ID": gridTableId,
      Action: input.id === undefined ? "Created" : "Updated",
      "Has Description": !!input.description?.trim(),
      "Is Shared": input.isShared,
      Source: source,
    })

  const handleSaveView = async (id: number | undefined, source: ViewSaveSource) => {
    const resolvedGridState =
      getCurrentState() ?? currentView?.gridState ?? getInitialState() ?? ({} as GridState)

    const input: SaveViewInput = {
      id,
      name: viewName,
      description: viewDescription.trim() ? viewDescription : null,
      isShared: isGlobalView,
      filters: filters,
      page,
      aggregationMethods: validateAndNormalizeAggregations(aggregations),
      warehouseId: selectedWarehouseId ?? null,
      channel: selectedChannel ?? null,
      gridState: normalizeGridStateForSave(resolvedGridState),
    }
    await saveView.mutateAsync(input, {
      onSuccess: (newView) => {
        setCurrentView(newView)
        // The apply-time baseline predates this save and would shadow the fresh blob.
        if (gridTableId) {
          clearAppliedViewBaseline(gridTableId)
          // Deferred so setCurrentView commits first.
          setTimeout(() => signalGridValueChange(gridTableId), 0)
        }
      },
    })
    trackViewSaved(input, source)
  }

  /**
   * Update `view` with the dialog's name/description/visibility edits. The active view also
   * captures the live grid state; any other view keeps its stored layout and the selection
   * stays put.
   */
  const handleUpdateView = async (view: SavedTableView, source: ViewSaveSource) => {
    if (view.id !== undefined && view.id === currentView?.id) {
      await handleSaveView(view.id, source)
      return
    }
    // Only name/description/visibility come from the dialog; everything else is the view's
    // stored state (client-only fields like `type` are stripped by the input schema).
    const input: SaveViewInput = {
      ...view,
      name: viewName,
      description: viewDescription.trim() ? viewDescription : null,
      isShared: isGlobalView,
      aggregationMethods: validateAndNormalizeAggregations(view.aggregationMethods),
    }
    await saveView.mutateAsync(input)
    trackViewSaved(input, source)
  }

  const finalViews: SavedTableView[] = useMemo(
    () =>
      (savedViews ?? [])
        .map((v) => ({ ...v, type: resolveViewScope(v, userId) }))
        .sort((a, b) => {
          if (a.type === "personal" && b.type !== "personal") return -1
          if (a.type !== "personal" && b.type === "personal") return 1
          return a.name.localeCompare(b.name)
        }),
    [savedViews, userId],
  )

  // Views are only supported on pages registered in TABLE_VIEW_META
  const supportsViews = !!tableView

  // On initial load, re-apply the view once the grid API becomes available.
  // This ensures gridState (column order, pinning, sizing) is applied even after a hard reload,
  // because the grid API isn't ready when the hook mounts.
  const initialViewAppliedRef = useRef(false)
  useEffect(() => {
    initialViewAppliedRef.current = false
  }, [gridTableId])

  useEffect(() => {
    if (!applyOnMount || !gridTableId) return

    // Read directly from sessionStorage to avoid stale closure: useSessionStorage's state
    // update for the new key is async (batched), so `currentView` may still hold the previous
    // tab's value when this effect fires — locationId and gridTableId change together on
    // tab navigation, but the sessionStorage re-read hasn't propagated yet.
    const stored = sessionStorage.getItem(locationId)
    let freshView: SavedTableView | undefined = undefined
    if (stored && stored !== "undefined") {
      try {
        freshView = JSON.parse(stored) as SavedTableView
      } catch {
        // corrupt sessionStorage entry — ignore and proceed with no view
      }
    }

    if (!freshView) return

    // An explicit `?view=` outranks this tab's stored slot; the caller applies that target once the
    // saved views load. The slot stays put, so a param naming nothing available falls back to it.
    if (viewParam !== undefined && !targetNamesView(parseViewParam(viewParam), freshView.id)) return

    const tryApply = () => {
      if (initialViewAppliedRef.current) return
      const api = getGridApi()
      if (api && !api.isDestroyed?.()) {
        initialViewAppliedRef.current = true
        viewChange(freshView, { isMountRestore: true })
      }
    }

    tryApply()
    if (!initialViewAppliedRef.current) {
      return subscribeGridApiChange(gridTableId, tryApply)
    }
  }, [gridTableId, applyOnMount]) // intentionally runs once per table

  // Subscribe to view change signals (fired by any useView instance on the same page).
  // Uses a ref so the subscription doesn't re-create when the callback identity changes.
  const onViewChangeRef = useRef(onViewChangeCallback)

  useEffect(() => {
    onViewChangeRef.current = onViewChangeCallback
  }, [onViewChangeCallback])

  useEffect(() => {
    if (!gridTableId || !onViewChangeRef.current) return
    return subscribeViewChange(gridTableId, () => onViewChangeRef.current?.())
  }, [gridTableId])

  return {
    currentView,
    isCurrentViewChanged,
    hasActiveGridFilter,
    backToDefault,
    tableView,
    gridTableId,
    locationId,
    setCurrentView,
    viewChange,
    isGlobalView,
    viewName,
    setViewName,
    viewDescription,
    setViewDescription,
    handleSaveView,
    handleUpdateView,
    setIsGlobalView,
    refetchViews,
    refreshViewsIfStale,
    savedViews,
    finalViews,
    supportsViews,
  }
}
